import { getBackendUrl } from "../lib/utils";
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { UploadCloud, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const HumanResources = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', department: '', cover_letter: ''
  });

  const [data, setData] = useState({
    title: "İnsan Kaynakları",
    subtitle: "Kurumsal",
    description: "Dinamik, yenilikçi ve sürekli gelişen ekibimizin bir parçası olmak için başvurularınızı bekliyoruz.",
    about_title: "Bizimle Çalışmak",
    about_description: "İSİLKOOP olarak, başarımızın ardındaki en büyük gücün çalışanlarımız olduğuna inanıyoruz. Çalışanlarımıza eşit fırsatlar sunan, gelişimi destekleyen ve başarıyı ödüllendiren bir çalışma ortamı yaratmayı hedefliyoruz.",
    benefits_title: "Neler Sunuyoruz?",
    benefits: [
      "Sürekli Eğitim ve Gelişim Fırsatları",
      "Modern ve Güvenli Çalışma Ortamı",
      "Kariyer Planlaması",
      "Performans Odaklı Ödüllendirme"
    ],
    positions_title: "Açık Pozisyonlar",
    positions_description: "Şu an için aktif bir ilan bulunmamaktadır ancak genel başvurularınız her zaman değerlendirmeye alınmaktadır.",
    positions: []
  });

  const [recaptchaSettings, setRecaptchaSettings] = useState({ enabled: false, site_key: '' });
  const [recaptchaToken, setRecaptchaToken] = useState('');

  useEffect(() => {
    const backendUrl = getBackendUrl();
    axios.get(`${backendUrl}/api/settings`)
      .then(res => {
        if (res.data.hr_page) {
          setData(res.data.hr_page);
        }
        if (res.data.recaptcha_settings) {
          const rSettings = res.data.recaptcha_settings;
          setRecaptchaSettings(rSettings);
          
          if ((rSettings.enabled === true || rSettings.enabled === 'true' || rSettings.enabled === 1) && rSettings.site_key) {
            // Load script dynamically
            if (!document.getElementById('recaptcha-script')) {
              const script = document.createElement('script');
              script.id = 'recaptcha-script';
              script.src = 'https://www.google.com/recaptcha/api.js';
              script.async = true;
              script.defer = true;
              document.body.appendChild(script);
            }
            
            // Expose callback on window
            window.onHrRecaptchaSuccess = (token) => {
              setRecaptchaToken(token);
            };
            window.onHrRecaptchaExpired = () => {
              setRecaptchaToken('');
            };
          }
        }
      })
      .catch(console.error);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((recaptchaSettings.enabled === true || recaptchaSettings.enabled === 'true' || recaptchaSettings.enabled === 1) && recaptchaSettings.site_key && !recaptchaToken) {
      alert("Lütfen robot olmadığınızı doğrulayın.");
      return;
    }
    setLoading(true);
    const postData = new FormData();
    postData.append('name', formData.name);
    postData.append('phone', formData.phone);
    postData.append('email', formData.email);
    postData.append('department', formData.department);
    postData.append('cover_letter', formData.cover_letter);
    postData.append('recaptcha_token', recaptchaToken);
    if (file) postData.append('cv_file', file);

    try {
      const backendUrl = getBackendUrl();
      await axios.post(`${backendUrl}/api/hr-applications`, postData);
      setIsSubmitted(true);
      setRecaptchaToken('');
      if (window.grecaptcha) {
        window.grecaptcha.reset();
      }
    } catch (err) {
      alert("Gönderim sırasında hata oluştu. Lütfen CV dosyanızı kontrol edin.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 pb-24 min-h-screen bg-slate-50">
      <div className="container mx-auto px-6 max-w-6xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-16">
          <h4 className="text-blue-600 font-semibold uppercase tracking-wider text-sm mb-3">{data.subtitle}</h4>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">{data.title}</h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            {data.description}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="space-y-8">
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">{data.about_title}</h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {data.about_description}
              </p>
              <h3 className="font-semibold text-slate-900 mb-3 text-lg">{data.benefits_title}</h3>
              <ul className="space-y-3">
                {data.benefits && data.benefits.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-600">
                    <CheckCircle2 size={18} className="text-blue-500 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-blue-50 p-8 rounded-3xl border border-blue-100">
              <h3 className="text-xl font-bold text-blue-900 mb-2">{data.positions_title}</h3>
              {data.positions && data.positions.length > 0 ? (
                <ul className="space-y-2.5 mt-4">
                  {data.positions.map((pos, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-blue-800 text-sm font-semibold">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                      {pos}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-blue-700 text-sm mb-4">{data.positions_description}</p>
              )}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
            <div className="bg-white p-8 md:p-10 rounded-3xl shadow-lg shadow-blue-900/5 border border-slate-100">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">İş Başvuru Formu</h2>
              
              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center text-center py-12 px-4 h-full">
                  <div className="w-20 h-20 bg-green-100 text-green-500 rounded-full flex items-center justify-center mb-6">
                    <CheckCircle2 size={40} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Başvurunuz Alındı!</h3>
                  <p className="text-slate-600">İlginiz için teşekkür ederiz. Özgeçmişiniz İnsan Kaynakları departmanımız tarafından incelenip uygun bulunması halinde sizinle iletişime geçilecektir.</p>
                  <button onClick={() => setIsSubmitted(false)} className="mt-8 text-blue-600 font-semibold hover:underline">Yeni Başvuru Yap</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">Adınız Soyadınız *</label>
                      <input required type="text" value={formData.name} onChange={e=>setFormData({...formData, name:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 bg-slate-50 focus:bg-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">Telefon Numaranız *</label>
                      <input required type="tel" value={formData.phone} onChange={e=>setFormData({...formData, phone:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 bg-slate-50 focus:bg-white" />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">E-Posta Adresiniz *</label>
                    <input required type="email" value={formData.email} onChange={e=>setFormData({...formData, email:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 bg-slate-50 focus:bg-white" />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Başvurulan Departman</label>
                    <select value={formData.department} onChange={e=>setFormData({...formData, department:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 bg-slate-50 focus:bg-white text-slate-700">
                      <option value="">Genel Başvuru</option>
                      {data.positions && data.positions.length > 0 ? (
                        data.positions.map((pos, i) => (
                          <option key={i} value={pos}>{pos}</option>
                        ))
                      ) : (
                        <>
                          <option value="muhendislik">Mühendislik & Mimari</option>
                          <option value="saha">Saha Operasyonları</option>
                          <option value="finans">Finans & Muhasebe</option>
                          <option value="satis">Satış & Pazarlama</option>
                        </>
                      )}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Ön Yazı</label>
                    <textarea rows={4} value={formData.cover_letter} onChange={e=>setFormData({...formData, cover_letter:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 bg-slate-50 focus:bg-white resize-none"></textarea>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Özgeçmiş (CV) Yükle *</label>
                    <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:bg-slate-50 transition-colors cursor-pointer group">
                      <input type="file" required onChange={e=>setFile(e.target.files[0])} className="hidden" id="cv-upload" accept=".pdf,.doc,.docx" />
                      <label htmlFor="cv-upload" className="cursor-pointer flex flex-col items-center gap-2">
                        <UploadCloud size={32} className="text-slate-400 group-hover:text-blue-500 transition-colors" />
                        <span className="text-sm text-slate-600 font-medium">{file ? file.name : "Tıklayın veya dosyanızı buraya sürükleyin"}</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 text-xs text-slate-600 font-medium my-4">
                    <input 
                      required 
                      type="checkbox" 
                      id="kvkk-consent" 
                      className="mt-1 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer" 
                    />
                    <label htmlFor="kvkk-consent" className="cursor-pointer select-none leading-relaxed">
                      <Link to="/sayfa/kvkk-aydinlatma-metni" target="_blank" className="text-blue-600 hover:underline font-bold">KVKK Aydınlatma Metni</Link> ve <Link to="/sayfa/gizlilik-politikasi" target="_blank" className="text-blue-600 hover:underline font-bold">Gizlilik Politikası</Link>'nı okudum, kabul ediyorum.
                    </label>
                  </div>

                  { (recaptchaSettings.enabled === true || recaptchaSettings.enabled === 'true' || recaptchaSettings.enabled === 1) && recaptchaSettings.site_key && (
                    <div className="my-6 flex justify-start">
                      <div 
                        className="g-recaptcha" 
                        data-sitekey={recaptchaSettings.site_key}
                        data-callback="onHrRecaptchaSuccess"
                        data-expired-callback="onHrRecaptchaExpired"
                      ></div>
                    </div>
                  )}

                  <button type="submit" disabled={loading} className="w-full py-4 bg-primary hover:bg-blue-600 text-white rounded-xl font-bold transition-all shadow-md disabled:opacity-50">
                    {loading ? 'Gönderiliyor...' : 'Başvuruyu Gönder'}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default HumanResources;
